// src/generators/.../SolveSimpleLinearEquationTask.js
import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* === Helpers === */
const rand = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const gcd = (a, b) => (b === 0 ? Math.abs(a) : gcd(b, a % b));
const simplifyFrac = (p, q) => {
  if (q < 0) { p = -p; q = -q; }
  const g = gcd(Math.abs(p), Math.abs(q));
  return [p / g, q / g];
};

const formatCoeffX = (a) => (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
const formatEq = (a, b, c) => {
  const aStr = formatCoeffX(a);
  if (b === 0) return `${aStr} = ${c}`; // nie pokazuj +0
  const bStr = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
  return `${aStr} ${bStr} = ${c}`;
};

const fracLatexSigned = (p, q) => {
  if (q === 1) return `${p}`;
  const neg = (p < 0) !== (q < 0);
  const a = Math.abs(p), b = Math.abs(q);
  return `${neg ? "-" : ""}\\tfrac{${a}}{${b}}`;
};

const bestLatex = (p, q) => {
  let [pp, qq] = simplifyFrac(p, q);
  if (qq === 1) return `${pp}`;
  const absP = Math.abs(pp), absQ = Math.abs(qq);
  const neg = pp < 0;
  if (absP > absQ) {
    const whole = Math.floor(absP / absQ);
    const rem = absP % absQ;
    if (rem === 0) return `${neg ? "-" : ""}${whole}`;
    return `${neg ? "-" : ""}${whole}\\,\\tfrac{${rem}}{${absQ}}`;
  }
  return `${pp < 0 ? "-" : ""}\\tfrac{${absP}}{${absQ}}`;
};

// luźny parser liczby (wspiera przecinek dziesiętny i zapisy ułamkowe)
const parseLooseNumber = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim().replace(",", ".");
  if (!s) return null;
  // mixed "a b/c"
  let m = s.match(/^([+-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const [, sign, A, B, C] = m;
    const v = (Number(A) + Number(B) / Number(C)) * (sign === "-" ? -1 : 1);
    return Number.isFinite(v) ? v : null;
  }
  // fraction "a/b"
  m = s.match(/^([+-]?)\s*(\d+)\s*\/\s*(\d+)$/);
  if (m) {
    const [, sign, P, Q] = m;
    if (Number(Q) === 0) return null;
    const v = (Number(P) / Number(Q)) * (sign === "-" ? -1 : 1);
    return Number.isFinite(v) ? v : null;
  }
  // decimal
  const v = Number(s);
  return Number.isFinite(v) ? v : null;
};

/* === GENERATE TASK === */
export function generateTask() {
  const pickSigned = () => (Math.random() < 0.5 ? -1 : 1) * rand(1, 19);

  let a = pickSigned();
  if (a === 0) a = 1;
  const b = pickSigned();
  const c = pickSigned();

  const num = c - b;
  const den = a;
  const [solP, solQ] = simplifyFrac(num, den);

  return {
    type: "SolveSimpleLinearEquationTask",
    data: {
      a, b, c, solP, solQ,
      movedStr: b >= 0 ? `${c} - ${b}` : `${c} + ${Math.abs(b)}`,
      simplifiedRhs: num
    }
  };
}

/* === RENDER TASK === */
export function renderTask({ data }) {
  const { a, b, c } = data;
  return (
    <p>
      Rozwiąż równanie: <BlockMath math={formatEq(a, b, c)} />
    </p>
  );
}

/* === INPUT === */
export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer || ""}
      onChange={onChange}
      disabled={disabled}
      label="x"
      placeholder="-7/4 lub -1 1/2"
    />
  );
}

/* === CHECK ANSWER === */
export function checkAnswer({ data }, userAnswer) {
  const { solP, solQ } = data;
  const expected = solP / solQ;

  // preferuj ścisłe porównanie frakcyjne, jeśli mamy p,q
  const up = userAnswer?.p;
  const uq = userAnswer?.q;
  if (Number.isInteger(up) && Number.isInteger(uq) && uq !== 0) {
    const [pp, qq] = simplifyFrac(up, uq);
    const isCorrect = (pp === solP && qq === solQ);
    return { isCorrect, expected, actual: pp / qq };
  }

  // inaczej – porównaj liczbowo (wspieramy przecinek dziesiętny)
  const numeric =
    (typeof userAnswer?.parsed === "number" ? userAnswer.parsed : null) ??
    parseLooseNumber(typeof userAnswer === "object" ? userAnswer?.raw : userAnswer);

  const isCorrect =
    numeric != null &&
    Number.isFinite(numeric) &&
    Math.abs(numeric - expected) <= 1e-9 * Math.max(1, Math.abs(expected));

  return { isCorrect, expected, actual: numeric ?? NaN };
}

/* === ANSWER === */
export function renderAnswer({ data }) {
  return <InlineMath math={`x = ${bestLatex(data.solP, data.solQ)}`} />;
}

/* === USER ANSWER (zachowujemy styl wpisu) === */

// Zamienia surowy string użytkownika na LaTeX, zachowując JEGO styl zapisu.
function rawToLatexRespectingStyle(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();

  // 1) mixed number:  a b/c   lub   -a b/c
  let m = s.match(/^([+-]?)\s*(\d+)\s+(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const [, sign, A, B, C] = m;
    const neg = sign === "-";
    const a = Number(A), b = Number(B), c = Number(C);
    if (c !== 0) {
      const frac = `\\tfrac{${b}}{${c}}`;
      return `${neg ? "-" : ""}${a}${b ? `\\,${frac}` : ""}`;
    }
  }

  // 2) zwykły ułamek:  p/q  lub  -p/q
  m = s.match(/^([+-]?)\s*(\d+)\s*\/\s*(\d+)\s*$/);
  if (m) {
    const [, sign, P, Q] = m;
    const neg = sign === "-";
    const p = Number(P), q = Number(Q);
    if (q !== 0) {
      return `${neg ? "-" : ""}\\tfrac{${p}}{${q}}`;
    }
  }

  // 3) liczba dziesiętna z przecinkiem/kropką (pokazuj z przecinkiem)
  m = s.match(/^([+-]?)\s*\d+(?:[.,]\d+)?\s*$/);
  if (m) {
    return s.replace(".", "{,}");
  }

  // 4) fallback – zostaw jak jest, tylko zamień kropkę na przecinek
  return s.replace(".", "{,}");
}

export function renderUserAnswer(userAnswer) {
  // Preferencja: jeśli mamy raw — pokazujemy w dokładnie takim stylu
  const raw = typeof userAnswer === "object" ? userAnswer?.raw : userAnswer;
  if (typeof raw === "string" && raw.trim() !== "") {
    const latex = rawToLatexRespectingStyle(raw);
    return <InlineMath math={`x = ${latex}`} />;
  }

  // Jeśli brak raw, ale mamy p,q — pokaż p/q (nie konwertuj do mieszanego)
  if (Number.isInteger(userAnswer?.p) && Number.isInteger(userAnswer?.q) && userAnswer.q !== 0) {
    const p = userAnswer.p, q = userAnswer.q;
    const neg = (p < 0) !== (q < 0);
    const a = Math.abs(p), b = Math.abs(q);
    return <InlineMath math={`x = ${neg ? "-" : ""}\\tfrac{${a}}{${b}}`} />;
  }

  // Jeśli mamy parsed — liczba z przecinkiem
  if (typeof userAnswer?.parsed === "number" && Number.isFinite(userAnswer.parsed)) {
    const s = String(userAnswer.parsed).replace(".", "{,}");
    return <InlineMath math={`x = ${s}`} />;
  }

  return <i>(brak odpowiedzi)</i>;
}

/* === EXPLANATION === */
export function renderExplanation({ data }) {
  const { a, b, c, solP, solQ, movedStr, simplifiedRhs } = data;
  const aText = (a === 1 ? "x" : a === -1 ? "-x" : `${a}x`);
  const bLatex = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
  const bAbs = Math.abs(b);
  const opVerb = b >= 0 ? "Odejmij obustronnie" : "Dodaj obustronnie";
  const movedFromSign = b >= 0 ? `+${b}` : `${b}`;
  const movedToSign = b >= 0 ? `-${b}` : `+${Math.abs(b)}`;
  const op1 = b >= 0 ? `- ${b}` : `+ ${bAbs}`;
  const rawFrac = simplifiedRhs % a === 0 ? `${simplifiedRhs / a}` : fracLatexSigned(simplifiedRhs, a);
  const best = bestLatex(solP, solQ);
  const showThird = rawFrac !== best;
  const aPar = a < 0 ? `(${a})` : `${a}`;

  return (
    <div className="space-y-2" style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> {opVerb} {bAbs}{" "}
        <span style={{ opacity: 0.85 }}>
          (równoważnie: przenieś <InlineMath math={`${movedFromSign}`} /> na drugą stronę
          ze zmianą znaku <InlineMath math={`${movedToSign}`} />).
        </span>
      </p>
      <BlockMath math={`${aText} ${bLatex} = ${c}\\;\\Big|\\ ${op1}`} />
      <BlockMath math={`${aText} = ${movedStr}`} />
      <BlockMath math={`${aText} = ${simplifiedRhs}`} />

      <p>
        <strong>Krok 2:</strong> Podziel obie strony przez <InlineMath math={aPar} />{" "}
        <span style={{ opacity: 0.85 }}>(to współczynnik przy <InlineMath math="x" />).</span>
      </p>
      <BlockMath math={`${aText} = ${simplifiedRhs}\\;\\Big|\\ :\\ ${aPar}`} />
      <BlockMath math={`x = ${rawFrac}`} />
      {showThird && <BlockMath math={`x = ${best}`} />}
    </div>
  );
}
